import React from 'react';
import {
  Button,
  Flex,
  Label,
  SelectField,
  Text,
  TextAreaField,
} from '@aws-amplify/ui-react';
import FlexFormContainer from '../common/FlexFormContainer';
import { useForm, Controller } from 'react-hook-form';
import { useAuthenticator } from '@aws-amplify/ui-react';
import { useCareNotesDirectPostMutation } from './api/useCareNotesDirectMutation';
import { useNavigate } from 'react-router-dom';
import { useUserQuery } from '../common/useUserQuery';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { formatDate } from '../common/useDateFunctions';
import { DatePicker } from 'rsuite';

const date = z.union([
  z.number({ message: 'Invalid Format. YYYY-MM-DD Format.' }),
  z.string({ message: 'Invalid Format. YYYY-MM-DD Format.' }),
  z.date({ message: 'Invalid Format. YYYY-MM-DD Format.' }),
]);

const postRequest = z.object({
  visitSummary: z.string({ message: 'Visit Summary is Required' }),
  healthSummary: z.string({ message: 'Health Summary is Required' }),
  overallStatus: z.string({ message: 'Status is Required' }),
  careDate: z.optional(date),
});

export default function CareNotesAddPage() {
  const navigate = useNavigate();
  const { data: userQueryData } = useUserQuery();
  const { user } = useAuthenticator((context) => [context.user]);
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      careDate: '',
    },
    resolver: zodResolver(postRequest),
  });
  const { mutate: careNotesDirectPostMutation } =
    useCareNotesDirectPostMutation();
  const onSubmit = (formValues) => {
    let params = {
      patientUserId: userQueryData?.patientUserId,
      caregiverUserId: user?.userId,
      visitSummary: formValues.visitSummary,
      healthSummary: formValues.healthSummary,
      overallStatus: formValues.overallStatus,
    };
    if (formValues.careDate != null && formValues.careDate !== '') {
      const formatEndDate = new Date(formValues.careDate);
      params = {
        ...params,
        careDate: formatDate(formatEndDate),
      };
    }
    careNotesDirectPostMutation(params);
    reset();
    navigate('/user/hr/care-notes');
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <FlexFormContainer headerTitle={'Add Care Note'}>
        <Flex
          direction="row"
          justifyContent="flex-start"
          alignItems="flex-start"
        >
          <Controller
            name="visitSummary"
            control={control}
            render={({ field }) => (
              <TextAreaField
                minWidth="300px"
                label="* Visit summary"
                {...field}
                value={field.value}
                errorMessage={errors.visitSummary?.message}
                hasError={!!errors.visitSummary}
              />
            )}
          />
        </Flex>
        <Flex
          direction="row"
          justifyContent="flex-start"
          alignItems="flex-start"
        >
          <Controller
            name="healthSummary"
            control={control}
            render={({ field }) => (
              <TextAreaField
                minWidth="300px"
                label="* Health summary"
                {...field}
                value={field.value}
                errorMessage={errors.healthSummary?.message}
                hasError={!!errors.healthSummary}
              />
            )}
          />
        </Flex>
        <Flex
          direction="row"
          justifyContent="flex-start"
          alignItems="flex-start"
        >
          <Controller
            name="overallStatus"
            control={control}
            render={({ field }) => (
              <SelectField
                minWidth="300px"
                label="* Overall Status"
                placeholder="Select"
                {...field}
                value={field.value}
                errorMessage={errors.overallStatus?.message}
                hasError={!!errors.overallStatus}
              >
                <option value="GOOD">Good</option>
                <option value="WATCHING">Watching</option>
                <option value="NEEDS_ATTENTION">Needs Attention</option>
                <option value="EMERGENCY">Emergency</option>
              </SelectField>
            )}
          />
        </Flex>
        <Label htmlFor="careDate">Date of Care</Label>
        <Controller
          name="careDate"
          control={control}
          render={({ field }) => (
            <>
              <DatePicker
                id={field.name}
                onChange={(value) => field.onChange(value)}
                value={field.value}
                format="yyyy-MM-dd"
                oneTap
                style={{ width: '300px' }}
                size="lg"
              />
              {!!errors.careDate && (
                <Text
                  variation="error"
                  as="p"
                  lineHeight="0.5em"
                  fontWeight={400}
                  fontSize="0.8em"
                  fontStyle="normal"
                  textDecoration="none"
                  width="30vw"
                  style={{ marginLeft: '10px' }}
                >
                  {errors.careDate.message}
                </Text>
              )}
            </>
          )}
        />

        <Flex gap="18px" direction="column">
          <Button variation="primary" type="submit">
            Save
          </Button>
        </Flex>
      </FlexFormContainer>
    </form>
  );
}
